import { uiText } from "@/lib/i18n/uiText";
import { useParams, Navigate, Link } from "@/lib/router-compat";
import { Card } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { BookOpen, Clock, ArrowRight } from "lucide-react";
import { GUIDES, findGuide } from "@/lib/seoClusters";
import { InternalLinks } from "@/components/seo/InternalLinks";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { useSEO } from "@/lib/useSEO";
import { useLocalizedGuide } from "@/hooks/useLocalizedGuide";
import { faqPageJsonLd, breadcrumbJsonLd, articleJsonLd, organizationJsonLd } from "@/lib/seoSchema";

export default function GuideDetail() {
  const { slug = "" } = useParams();
  const source = findGuide(slug);
  // Localize prose (title/intro/body/FAQs) — English is a no-op, other locales
  // fetch cached translations so both visible copy and JSON-LD rich snippets
  // match the served language. Call the hook unconditionally to satisfy Rules
  // of Hooks; a Navigate is emitted below if the slug is unknown.
  const { guide: localized } = useLocalizedGuide(
    source ?? { slug, title: "", label: "", description: "", intro: "" } as any,
  );
  const guide = source ? localized : undefined;

  const articleNode = guide
    ? articleJsonLd({
        headline: guide.label,
        description: guide.description,
        path: `/guides/${slug}`,
        datePublished: "2025-01-01T00:00:00Z",
        dateModified: new Date().toISOString(),
        section: "Betting Guides",
      })
    : undefined;

  const jsonLd = guide
    ? [
        organizationJsonLd(),
        articleNode!,
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Betting Guides", path: "/guides" },
          { name: guide.label, path: `/guides/${slug}` },
        ]),
        ...(guide.faqs && guide.faqs.length ? [faqPageJsonLd(guide.faqs)] : []),
      ]
    : undefined;

  useSEO({
    title: guide?.title ?? "Betting Guide",
    description: guide?.description!,
    canonicalPath: `/guides/${slug}`,
    ogType: "article",
    jsonLd: jsonLd!,
  });

  if (!guide) return <Navigate to="/guides" replace />;

  // Related guides first, then any remaining guides.
  const relatedSlugs = guide.related ?? [];
  const orderedOthers = [
    ...relatedSlugs.map((s) => GUIDES.find((g) => g.slug === s)).filter(Boolean),
    ...GUIDES.filter((g) => g.slug !== guide.slug && !relatedSlugs.includes(g.slug)),
  ].filter((g): g is (typeof GUIDES)[number] => !!g && g.slug !== guide.slug);

  return (
    <div className="space-y-8 animate-fade-in">
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "Betting Guides", path: "/guides" },
          { name: guide.label, path: `/guides/${slug}` },
        ]}
      />


      <header className="space-y-3">
        <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
          <BookOpen className="h-7 w-7 text-primary shrink-0" />
          {uiText(guide.label)}
        </h1>
        {guide.readingTime && (
          <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            {guide.readingTime}
          </p>
        )}
        <p className="text-lg leading-relaxed text-muted-foreground max-w-3xl">{guide.intro}</p>
      </header>

      {/* Long-form body */}
      {guide.body && guide.body.length > 0 && (
        <article className="max-w-3xl space-y-8">
          {guide.body.map((section) => (
            <section key={section.heading} className="space-y-3">
              <h2 className="text-xl font-semibold">{uiText(section.heading)}</h2>
              {section.paragraphs.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-muted-foreground">
                  {p}
                </p>
              ))}
            </section>
          ))}
        </article>
      )}

      {/* FAQ */}
      {guide.faqs && guide.faqs.length > 0 && (
        <section className="max-w-3xl space-y-4">
          <h2 className="text-2xl font-bold">{uiText("Frequently Asked Questions")}</h2>
          <Accordion type="single" collapsible className="w-full">
            {guide.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left">{uiText(f.question)}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {uiText(f.answer)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* Related guides */}
      {orderedOthers.length > 0 && (
        <section className="max-w-3xl space-y-3">
          <h2 className="text-xl font-semibold">{uiText("Continue Learning")}</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {orderedOthers.map((g) => (
              <Link
                key={g.slug}
                to={`/guides/${g.slug}`}
                className="group flex items-start justify-between gap-3 rounded-lg border border-border/50 bg-card/60 p-4 transition-colors hover:border-primary/40"
              >
                <span>
                  <span className="block font-medium">{uiText(g.label)}</span>
                  <span className="block text-sm text-muted-foreground line-clamp-2">
                    {uiText(g.description)}
                  </span>
                </span>
                <ArrowRight className="h-4 w-4 mt-1 shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
              </Link>
            ))}
          </div>
        </section>
      )}

      <InternalLinks title={uiText("Put It Into Practice")} excludeSlug={guide.slug} />
    </div>
  );
}
